import { useRef } from "react"
import { IconAlertTriangle, IconChevronDown, IconLoader2, IconPlayerPlay } from "@tabler/icons-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { useBenefitLifecycleTransitions } from "@/features/benefit-workspace/useBenefitProgramReads"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { api } from "@/lib/api"
import { saveBenefitChange } from "@/lib/benefit-save"
import { primaryLifecycleAction } from "@/lib/lifecycle-actions"
import type { AvailableTransition, BenefitDetailDto } from "@/lib/benefit-types"
import { useHasOpenBenefitEditors } from "./benefit-edit-scope"

const destructiveConfirmMessages: Record<string, string> = {
  Deactivate: "This will deactivate the benefit application. The application will no longer be actively processed.",
  Deny: "This will deny the benefit application. This action indicates the applicant is not eligible for benefits.",
}

/** The captured confirmation outlives both this component and the current action list. */
export function LifecycleActions({ benefit, available, onTransitionComplete }: {
  benefit: BenefitDetailDto; available: boolean; onTransitionComplete(updated: BenefitDetailDto): void
}) {
  const read = useBenefitLifecycleTransitions(benefit, available)
  const [confirmEditing, setConfirmEditing, capturedToken] = useBenefitEdit(benefit.concurrencyToken, "lifecycle")
  const [pending, setExecuting] = useBenefitSaving()
  const hasOpenEditor = useHasOpenBenefitEditors()
  const review = useBenefitReviewActive()
  const [confirmAction, setConfirmAction] = useBenefitDraftState("lifecycle", "action", null)
  const origin = useRef<HTMLElement | null>(null)
  const heading = useRef<HTMLHeadingElement | null>(null)
  const primaryButton = useRef<HTMLButtonElement | null>(null)
  const actions = read.readback?.availableTransitions ?? []
  const primary = read.readback ? primaryLifecycleAction(read.readback.currentState, actions) : null
  const others = actions.filter(action => action !== primary)
  const executing = pending || (hasOpenEditor && !confirmEditing)
  const confirmationAvailable = !!confirmAction && actions.filter(action => action.trigger === confirmAction.trigger).length === 1
  const close = () => { if (setConfirmEditing(false)) setConfirmAction(null) }

  const execute = async (trigger: string, label: string, token: string | null) => {
    // Recheck even if a previously enabled control's callback is invoked. A fresh read
    // permits an attempt, not adoption of its token into the retained confirmation.
    if (executing || !available || !read.readback || actions.filter(action => action.trigger === trigger).length !== 1 || !token) {
      toast.error("Reload the application before changing its lifecycle."); return
    }
    setExecuting(true)
    try {
      const result = await saveBenefitChange(benefit.id, onTransitionComplete, () => api.post<BenefitDetailDto>(
        `/api/benefitapplications/${benefit.id}/transitions`, { trigger, concurrencyToken: token },
      ), `${label} completed successfully`)
      if (result.saved || result.reloaded) close()
    } finally { setExecuting(false) }
  }
  const handleAction = (action: AvailableTransition) => {
    if (executing || !read.readback) return
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (action.isDestructive) {
      if (setConfirmEditing(true)) setConfirmAction({ ...action, token: benefit.concurrencyToken })
    } else void execute(action.trigger, action.label, benefit.concurrencyToken)
  }
  const returnFocus = () => {
    if (review) document.querySelector<HTMLElement>("[data-benefit-editor-focus]")?.focus()
    else if (origin.current?.isConnected && !origin.current.closest('[hidden], [inert]')) origin.current.focus()
    else if (primaryButton.current && !primaryButton.current.disabled) primaryButton.current.focus()
    else (heading.current ?? document.querySelector<HTMLElement>("[data-benefit-editor-focus]"))?.focus()
  }

  // Unavailable authority must not unhost the old confirmation or its Cancel action.
  if (!available && !confirmEditing) return null
  return <section aria-label="Legacy lifecycle actions" className="space-y-2">
    <h3 ref={heading} tabIndex={-1} className="sr-only">Legacy lifecycle actions</h3>
    {read.loading && <p role="status" className="text-sm text-muted-foreground">Loading lifecycle actions…</p>}
    {read.error && <p role="status" className="text-sm text-muted-foreground">{read.error} {read.canRetry && <Button variant="outline" size="sm" onClick={read.retry}>Retry lifecycle read</Button>}</p>}
    {read.readback?.completionBlockedReason && <p className="text-sm text-amber-700 dark:text-amber-300">{read.readback.completionBlockedReason}</p>}
    {primary && <div className="flex items-center gap-1">
      <Button ref={primaryButton} size="sm" variant={primary.isDestructive && !others.length ? "destructive" : "default"}
        className={others.length ? "rounded-r-none" : undefined} onClick={() => handleAction(primary)} disabled={executing || confirmEditing}>
        {executing ? <IconLoader2 className="size-4 mr-1.5 animate-spin" /> : <IconPlayerPlay className="size-4 mr-1.5" />}{primary.label}
      </Button>
      {others.length > 0 && <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild><Button size="sm" className="rounded-l-none border-l px-2" disabled={executing || confirmEditing} aria-label="More application actions"><IconChevronDown className="size-4" /></Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end">{others.map((action, index) => <div key={action.trigger}>
          {action.isDestructive && index > 0 && !others[index - 1]!.isDestructive && <DropdownMenuSeparator />}
          <DropdownMenuItem variant={action.isDestructive ? "destructive" : undefined} onClick={() => handleAction(action)}>
            {action.isDestructive && <IconAlertTriangle className="size-4 mr-1.5" />}{action.label}
          </DropdownMenuItem>
        </div>)}</DropdownMenuContent>
      </DropdownMenu>}
    </div>}
    <AlertDialog open={!!confirmAction && confirmEditing && !review} onOpenChange={open => { if (!open) close() }}>
      <AlertDialogContent onCloseAutoFocus={event => { event.preventDefault(); returnFocus() }}>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm: {confirmAction?.label}</AlertDialogTitle>
          <AlertDialogDescription>{confirmAction && (destructiveConfirmMessages[confirmAction.trigger] ?? `Are you sure you want to ${confirmAction.label.toLowerCase()} this benefit application?`)}</AlertDialogDescription>
        </AlertDialogHeader>
        {!confirmationAvailable && <p role="status" className="text-sm">This action is not currently available. Your original confirmation and token are retained; cancel or refresh the saved data.</p>}
        {read.error && read.canRetry && <Button variant="outline" onClick={read.retry}>Retry lifecycle read</Button>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-destructive text-white hover:bg-destructive/90" disabled={executing || !confirmationAvailable || !capturedToken}
            onClick={event => { event.preventDefault(); if (confirmAction) void execute(confirmAction.trigger, confirmAction.label, capturedToken) }}>
            {pending && <IconLoader2 className="size-4 mr-1.5 animate-spin" />}{confirmAction?.label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </section>
}
